package com.evoapps.evoshift.data.mapper

import com.evoapps.evoshift.domain.usecase.shift.ShiftFactory
import com.evoapps.evoshift.data.dto.roster.ShiftDto
import com.evoapps.evoshift.data.dto.roster.ShiftTimeDto
import com.evoapps.evoshift.data.dto.roster.ShiftBreakDto
import com.evoapps.evoshift.domain.model.ShiftBreak
import com.evoapps.evoshift.domain.model.ClockAudit
import com.evoapps.evoshift.domain.model.ClockSource
import com.evoapps.evoshift.domain.model.ClockStamp
import com.evoapps.evoshift.domain.model.LocationCheck
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ClockCheck
import com.evoapps.evoshift.domain.model.ClockDecision
import com.evoapps.evoshift.domain.model.ClockFlag
import com.evoapps.evoshift.domain.model.ClockReviewDecision
import com.evoapps.evoshift.domain.model.UnplannedClock
import com.evoapps.evoshift.domain.model.UnplannedKind
import com.evoapps.evoshift.domain.model.UnplannedStatus
import com.evoapps.evoshift.data.dto.roster.ClockReviewDto
import com.evoapps.evoshift.data.dto.roster.UnplannedDto
import com.evoapps.evoshift.data.dto.roster.ShiftSplitDto
import com.evoapps.evoshift.domain.model.ShiftSplit

class ShiftMapper(
    private val shiftTimeMapper: ShiftTimeMapper,
    private val conflictMetadataMapper: ConflictMetadataMapper,
    private val swapRequestMapper: SwapRequestMapper,
    private val metadataMapper: MetadataMapper
) {

    fun toDomain(dto: ShiftDto): Shift {
        return Shift(
            id = dto.id,
            rosterId = dto.rosterId,
            date = dto.date,
            dailyRecordId = dto.dailyRecordId,
            requiredRole = dto.requiredRole?.takeIf { it.isNotBlank() && it != ShiftFactory.LEGACY_DEFAULT_ROLE },
            requiredSkills = dto.requiredSkills,
            userId = dto.userId,
            userDisplayName = null, // Needs to be joined separately
            actualUserId = dto.actualUserId,
            isPublished = dto.isPublished,
            hasUnpublishedChanges = dto.hasUnpublishedChanges,
            version = dto.version,
            publishedUserId = dto.publishedUserId?.takeIf { it.isNotBlank() },
            plannedTime = shiftTimeMapper.toDomain(dto.planned),
            actualTime = if (dto.actual.start.isEmpty() && dto.actual.end.isEmpty()) null else shiftTimeMapper.toDomain(
                dto.actual
            ),
            swapRequest = dto.swapRequest?.let { swapRequestMapper.toDomain(it) },
            isOpen = dto.isOpen,
            conflictMetadata = dto.conflictMetadata?.let { conflictMetadataMapper.toDomain(it) },
            metadata = metadataMapper.toDomain(dto.metadata),
            absenceId = dto.absenceId,
            tags = dto.tags,
            clockAudit = clockAuditOf(dto.actual),
            breaks = breaksOf(dto.actual),
            notWorked = dto.notWorked,
            clockCheck = clockCheckOf(dto.actual),
            unplanned = dto.unplanned?.let(::unplannedOf),
            noShowReason = dto.noShowReason,
            split = dto.split?.let(::splitOf),
            borrowedFromWorkplaceId = dto.borrowedFromWorkplaceId?.takeIf { it.isNotBlank() }
        )
    }

    fun toDto(domain: Shift): ShiftDto {
        return ShiftDto(
            id = domain.id,
            rosterId = domain.rosterId,
            date = domain.date,
            dailyRecordId = domain.dailyRecordId,
            isPublished = domain.isPublished,
            hasUnpublishedChanges = domain.hasUnpublishedChanges,
            version = domain.version,
            publishedUserId = domain.publishedUserId,
            conflictMetadata = domain.conflictMetadata?.let { conflictMetadataMapper.toDto(it) },
            requiredRole = domain.requiredRole,
            requiredSkills = domain.requiredSkills,
            userId = domain.userId,
            actualUserId = domain.actualUserId,
            planned = shiftTimeMapper.toDto(domain.plannedTime),
            actual = withClockAudit(
                domain.actualTime?.let { shiftTimeMapper.toDto(it) } ?: ShiftTimeDto("", ""),
                domain.clockAudit
            ).copy(breaks = domain.actualTime?.let { breaksDto(domain.breaks) })
                .withClockCheck(domain.clockCheck),
            swapRequest = domain.swapRequest?.let { swapRequestMapper.toDto(it) },
            isOpen = domain.isOpen,
            metadata = metadataMapper.toDto(domain.metadata),
            absenceId = domain.absenceId,
            tags = domain.tags,
            notWorked = domain.notWorked,
            unplanned = domain.unplanned?.let(::unplannedDto),
            // SH-259: the managers' notes go to shift_manager_notes (ShiftManagerNotesDto), never
            // on the shift that colleagues may read; writing the field empty also clears what an
            // older app left there.
            noShowReason = null,
            split = domain.split?.let(::splitDto),
            borrowedFromWorkplaceId = domain.borrowedFromWorkplaceId
        )
    }

    companion object {
        /** SH-236: a split part; one that cannot be read (no group, unknown part) is no split. */
        fun splitOf(dto: ShiftSplitDto): ShiftSplit? = dto.takeIf { it.groupId.isNotEmpty() && it.part in 1..2 }?.let {
            ShiftSplit(it.groupId, it.part, it.handoverAt, it.originalPlannedEnd, it.originalActualEnd, it.reason, it.sickness, it.otherUserId, it.byUserId, it.at)
        }

        /**
         * SH-255: never whether the person fell ill: the shift is read by their colleagues where the
         * roster is open to them, and sickness is health data (GDPR art. 9). The sick leave itself,
         * when the manager records one, is an absence only they and the managers read. SH-259: nor
         * the manager's note on why it was split (shift_manager_notes).
         */
        fun splitDto(s: ShiftSplit): ShiftSplitDto =
            ShiftSplitDto(s.groupId, s.part, s.handoverAt, s.originalPlannedEnd, s.originalActualEnd, reason = null, sickness = false, s.otherUserId, s.byUserId, s.at)

        /** SH-134: the flags, the manager's decision and the photos of an `actual` map; null when none. */
        fun clockCheckOf(dto: ShiftTimeDto): ClockCheck? {
            val flags = dto.flags.orEmpty().mapNotNull { ClockFlag.fromName(it) }
            val review = dto.review?.let { r ->
                ClockDecision.fromName(r.decision)?.let {
                    ClockReviewDecision(it, r.flags.mapNotNull { f -> ClockFlag.fromName(f) }, r.start, r.end, r.by, r.byName, r.at)
                }
            }
            if (flags.isEmpty() && review == null && dto.startPhoto == null && dto.endPhoto == null) return null
            return ClockCheck(flags, review, dto.startPhoto, dto.endPhoto)
        }

        /** Keeps the review side as it is when a manager saves the whole shift. */
        fun ShiftTimeDto.withClockCheck(check: ClockCheck?): ShiftTimeDto = copy(
            flags = check?.flags?.map { it.name },
            review = check?.review?.let {
                ClockReviewDto(it.decision.name, it.flags.map { f -> f.name }, it.start, it.end, it.decidedBy, it.decidedByName, it.decidedAt)
            },
            startPhoto = check?.startPhotoPath,
            endPhoto = check?.endPhotoPath
        )

        fun unplannedOf(dto: UnplannedDto): UnplannedClock? {
            val kind = UnplannedKind.fromName(dto.kind) ?: return null
            val status = UnplannedStatus.fromName(dto.status) ?: return null
            return UnplannedClock(
                kind, dto.userId, dto.userName, dto.coveredUserId, dto.coveredUserName, status,
                ClockSource.entries.find { it.name == dto.via }, dto.at, dto.decidedBy, dto.decidedAt, dto.decidedByName
            )
        }

        fun unplannedDto(u: UnplannedClock): UnplannedDto = UnplannedDto(
            u.kind.name, u.userId, u.userName, u.coveredUserId, u.coveredUserName, u.status.name, u.via?.name, u.at,
            u.decidedBy, u.decidedByName, u.decidedAt
        )

        /** SH-116: the breaks of an `actual` map, in order; unreadable entries are skipped. */
        fun breaksOf(dto: ShiftTimeDto): List<ShiftBreak> = dto.breaks.orEmpty()
            .filter { it.start.isNotEmpty() }
            .map {
                ShiftBreak(
                    it.start, it.end, ClockSource.entries.find { s -> s.name == it.source },
                    LocationCheck.fromName(it.startLocationCheck), it.startDistanceM,
                    LocationCheck.fromName(it.endLocationCheck), it.endDistanceM
                )
            }
            .sortedBy { it.start }

        /** null when there are none (no empty list is written where there was none). */
        fun breaksDto(breaks: List<ShiftBreak>): List<ShiftBreakDto>? =
            breaks.takeIf { it.isNotEmpty() }?.map {
                ShiftBreakDto(
                    it.start, it.end, it.source?.name,
                    it.startLocationCheck?.name, it.startDistanceM, it.endLocationCheck?.name, it.endDistanceM
                )
            }

        /** The SH-107 stamps of an `actual` map; null when none was recorded (older shifts). */
        fun clockAuditOf(dto: ShiftTimeDto): ClockAudit? {
            val start = stamp(dto.startSource, dto.startKioskId, dto.startSyncedLate, dto.startDeviceTime)
                ?.copy(
                    locationCheck = LocationCheck.fromName(dto.startLocationCheck), distanceMeters = dto.startDistanceM,
                    driftMinutes = dto.startDrift?.minutes, driftServerAt = dto.startDrift?.serverAt
                )
            val end = stamp(dto.endSource, dto.endKioskId, dto.endSyncedLate, dto.endDeviceTime)
                ?.copy(
                    locationCheck = LocationCheck.fromName(dto.endLocationCheck), distanceMeters = dto.endDistanceM,
                    driftMinutes = dto.endDrift?.minutes, driftServerAt = dto.endDrift?.serverAt
                )
            if (start == null && end == null && dto.reportedBy == null) return null
            return ClockAudit(start, end, dto.reportedBy, dto.reportedAt)
        }

        fun withClockAudit(dto: ShiftTimeDto, audit: ClockAudit?): ShiftTimeDto = dto.copy(
            startSource = audit?.start?.source?.name,
            startKioskId = audit?.start?.kioskId,
            startSyncedLate = audit?.start?.syncedLate?.takeIf { it },
            startDeviceTime = audit?.start?.deviceTime,
            endSource = audit?.end?.source?.name,
            endKioskId = audit?.end?.kioskId,
            endSyncedLate = audit?.end?.syncedLate?.takeIf { it },
            endDeviceTime = audit?.end?.deviceTime,
            startLocationCheck = audit?.start?.locationCheck?.name,
            startDistanceM = audit?.start?.distanceMeters,
            endLocationCheck = audit?.end?.locationCheck?.name,
            endDistanceM = audit?.end?.distanceMeters,
            // SH-257: a manager's whole-shift save keeps who entered the hours.
            reportedBy = audit?.reportedBy,
            reportedAt = audit?.reportedAt
        )

        private fun stamp(source: String?, kioskId: String?, syncedLate: Boolean?, deviceTime: String?): ClockStamp? =
            ClockSource.entries.find { it.name == source }?.let { ClockStamp(it, kioskId, syncedLate == true, deviceTime) }
    }
}
